<script setup lang="ts">
import { onMounted, ref, watch } from 'vue';
import type {
  EntityOptions,
  GroupedEntityOptions,
} from '../../../../../Entities/Shelter/modules/KennelManager/types/kennel.types';
import { getKennelManagerLabelsHelper } from '@DataTableModule';

interface KennelManagerFieldModel {
  kennel_site_id?: string[];
  kennel_building_id?: string[];
  kennel_location_id?: string[];
  kennel_id?: string[];
}

const model = defineModel<KennelManagerFieldModel[]>({ default: [] });

const buildingModel = ref();
const buildingOptions = ref<EntityOptions[]>([]);
const kennelModel = ref();
const kennelOptions = ref<EntityOptions[] | GroupedEntityOptions[]>([]);
const loading = ref<boolean>(false);
const locationLoading = ref<boolean>(false);
const locationModel = ref();
const locationOptions = ref<EntityOptions[] | GroupedEntityOptions[]>([]);
const siteModel = ref();
const siteOptions = ref<EntityOptions[]>([]);

onMounted(() => getAnimalKennels());

watch(model, () => (!model.value?.length ? clearValues() : ''));

function clearValues() {
  siteModel.value = [];
  buildingModel.value = [];
  locationModel.value = [];
  kennelModel.value = [];
}

async function getAnimalKennels() {
  loading.value = true;

  const data = await getKennelManagerLabelsHelper(true);
  if (!data) return;

  siteOptions.value = data.sites;
  buildingOptions.value = data.buildings;
  locationOptions.value = data.locations;
  kennelOptions.value = data.kennels as GroupedEntityOptions[];

  const hasFilter = model.value?.length;
  if (hasFilter) applyFilterParams();

  loading.value = false;
}

function applyFilterParams() {
  const filter = model.value[0];

  if (filter.kennel_id?.length) {
    kennelModel.value = filterGroupedOptions(
      kennelOptions.value as GroupedEntityOptions[],
      filter.kennel_id
    );
    return;
  }

  if (filter.kennel_location_id?.length) {
    locationModel.value = filterGroupedOptions(
      locationOptions.value as GroupedEntityOptions[],
      filter.kennel_location_id
    );
    return;
  }

  if (filter.kennel_building_id?.length) {
    buildingModel.value = buildingOptions.value.filter((building: any) =>
      filter.kennel_building_id?.includes(building._id)
    );
    return;
  }

  if (filter.kennel_site_id?.length) {
    siteModel.value = siteOptions.value.filter((site: any) =>
      filter.kennel_site_id?.includes(site._id)
    );
    return;
  }
}

function filterGroupedOptions(groupedOptions: GroupedEntityOptions[], ids: string[]) {
  const selected: EntityOptions[] = [];

  for (const group of groupedOptions) {
    for (const item of group.items) {
      if (ids.includes(item._id)) {
        selected.push(item);
      }
    }
  }

  return selected;
}

function onChangeSite() {
  buildingModel.value = [];
  locationModel.value = [];
  kennelModel.value = [];

  if (!siteModel.value?.length) return (model.value = []);
  model.value = [
    {
      kennel_site_id: siteModel.value.map((site: any) => site._id),
    },
  ];
}

async function onChangeBuilding() {
  siteModel.value = [];
  locationModel.value = [];
  kennelModel.value = [];

  if (!buildingModel.value?.length) return (model.value = []);
  model.value = [
    {
      kennel_building_id: buildingModel.value.map((building: any) => building._id),
    },
  ];
}

function onChangeLocation() {
  siteModel.value = [];
  buildingModel.value = [];
  kennelModel.value = [];

  if (!locationModel.value?.length) return (model.value = []);
  model.value = [
    {
      kennel_location_id: locationModel.value.map((location: any) => location._id),
    },
  ];
}

function onChangeKennel() {
  siteModel.value = [];
  buildingModel.value = [];
  locationModel.value = [];

  if (!kennelModel.value?.length) return (model.value = []);
  model.value = [
    {
      kennel_id: kennelModel.value.map((kennel: any) => kennel._id),
    },
  ];
}
</script>

<template>
  <template v-if="siteOptions?.length">
    <div class="grid-item mt-2 w-full gap-1">
      <FloatLabel v-if="!loading">
        <label
          class="z-10 pl-2 text-sm leading-[1.0]"
          for="kennel_manager_site"
        >
          {{ $t('animals.individualPage.kennel.labels.site') }}
        </label>

        <AFieldMultiSelect
          v-model="siteModel"
          class="!h-[38px]"
          id="kennel_manager_site"
          optionLabel="label"
          :options="siteOptions"
          @change="onChangeSite"
        />
      </FloatLabel>

      <Skeleton
        v-else
        class="w-full rounded-md"
        height="2.25rem"
      />
    </div>

    <div class="grid-item mt-2 w-full gap-1">
      <FloatLabel v-if="!loading">
        <label
          class="z-10 pl-2 text-sm leading-[1.0]"
          for="kennel_manager_building"
        >
          {{ $t('animals.individualPage.kennel.labels.building') }}
        </label>

        <AFieldMultiSelect
          v-model="buildingModel"
          class="!h-[38px]"
          id="kennel_manager_building"
          optionLabel="label"
          :options="buildingOptions"
          @change="onChangeBuilding"
        />
      </FloatLabel>

      <Skeleton
        v-else
        class="w-full rounded-md"
        height="2.25rem"
      />
    </div>

    <div class="grid-item mt-2 w-full gap-1">
      <FloatLabel v-if="!loading && !locationLoading">
        <label
          class="z-10 pl-2 text-sm leading-[1.0]"
          for="kennel_manager_location"
        >
          {{ $t('animals.individualPage.kennel.labels.location') }}
        </label>

        <AFieldMultiSelect
          v-model="locationModel"
          class="!h-[38px]"
          id="kennel_manager_location"
          optionGroupChildren="items"
          optionGroupLabel="label"
          optionLabel="label"
          :options="locationOptions"
          @change="onChangeLocation"
        />
      </FloatLabel>

      <Skeleton
        v-else
        class="w-full rounded-md"
        height="2.25rem"
      />
    </div>

    <div class="grid-item mt-2 w-full gap-1">
      <FloatLabel v-if="!loading && !locationLoading">
        <label
          class="z-10 pl-2 text-sm leading-[1.0]"
          for="kennel_manager_kennel"
        >
          {{ $t('animals.individualPage.kennel.labels.kennel') }}
        </label>

        <AFieldMultiSelect
          v-model="kennelModel"
          filter
          class="!h-[38px]"
          id="kennel_manager_kennel"
          optionGroupChildren="items"
          optionGroupLabel="label"
          optionLabel="label"
          :options="kennelOptions"
          @change="onChangeKennel"
        />
      </FloatLabel>

      <Skeleton
        v-else
        class="w-full rounded-md"
        height="2.25rem"
      />
    </div>
  </template>

  <template v-else-if="!siteOptions?.length && loading">
    <div
      v-for="n in 4"
      :key="n"
      class="grid-item mt-2 w-full gap-1"
    >
      <Skeleton
        class="w-full rounded-md"
        height="2.25rem"
      />
    </div>
  </template>
</template>
